import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkles, ChevronDown } from "lucide-react";
import { goToAppStore } from "../lib/api";

const LINKS = [
  { label: "Why Us?", href: "#why", testid: "nav-why-link" },
  { label: "Services", href: "#services", testid: "nav-services-link" },
  {
    label: "How It Works",
    to: "/how-it-works",
    testid: "nav-how-link",
    dropdown: [
      { label: "The Precision Method", to: "/how-it-works", testid: "nav-how-method" },
      { label: "For Users", to: "/consultation", testid: "nav-how-users" },
      { label: "For Stylists", to: "/join-stylist", testid: "nav-how-stylists" },
      { label: "For Salons", to: "/join-salon", testid: "nav-how-salons" },
      { label: "For Influencers", to: "/how-it-works/influencers", testid: "nav-how-influencers" },
    ],
  },
  { label: "Shop", href: "/shop", testid: "nav-shop-link" },
  { label: "Journal", href: "#journal", testid: "nav-blog-link" },
  { label: "Reviews", href: "#reviews", testid: "nav-reviews-link" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FAF8F5]/85 backdrop-blur-xl border-b border-stylist-border shadow-[0_1px_30px_rgba(28,25,23,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img src="/logo.png" alt="Senior Stylist logo" className="h-12 w-auto object-contain shrink-0" />
            <span className="flex flex-col leading-none">
              <span className="font-serif text-2xl tracking-tight text-stylist-ink">Senior Stylist</span>
              <span className="eyebrow mt-1 group-hover:text-stylist-gold transition-colors">London · Est. 2026</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {LINKS.map((l) =>
              l.dropdown ? (
                <div key={l.label} className="relative group" data-testid={l.testid}>
                  <Link
                    to={l.to}
                    className="flex items-center gap-1 text-sm text-stylist-ink2 hover:text-stylist-ink transition-colors"
                  >
                    {l.label}
                    <ChevronDown className="w-3.5 h-3.5 group-hover:rotate-180 transition-transform duration-300" />
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                    <div className="bg-white border border-stylist-border rounded-lg shadow-xl py-2 min-w-[190px]">
                      {l.dropdown.map((d) => (
                        <Link
                          key={d.label}
                          to={d.to}
                          data-testid={d.testid}
                          className="block px-4 py-2.5 text-sm text-stylist-ink2 hover:text-stylist-gold hover:bg-stylist-bg2 transition-colors"
                        >
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={l.testid}
                  {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="text-sm text-stylist-ink2 hover:text-stylist-ink ss-link-underline"
                >
                  {l.label}
                </a>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={goToAppStore}
              data-testid="nav-subscribe-button"
              className="group flex items-center gap-2 rounded-full bg-stylist-ink text-white text-sm px-5 py-2.5 hover:bg-stylist-gold transition-colors duration-300"
            >
              <Sparkles className="w-4 h-4" />
              Subscribe today
            </button>
          </div>

          <button
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 text-stylist-ink"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#FAF8F5] border-b border-stylist-border"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) =>
                l.dropdown ? (
                  <div key={l.label} className="flex flex-col gap-2">
                    <Link
                      to={l.to}
                      data-testid={l.testid}
                      onClick={() => setOpen(false)}
                      className="text-base text-stylist-ink font-medium"
                    >
                      {l.label}
                    </Link>
                    {l.dropdown.map((d) => (
                      <Link
                        key={d.label}
                        to={d.to}
                        data-testid={d.testid}
                        onClick={() => setOpen(false)}
                        className="text-sm text-stylist-ink2 pl-4"
                      >
                        {d.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="text-base text-stylist-ink2"
                  >
                    {l.label}
                  </a>
                )
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  goToAppStore();
                }}
                data-testid="nav-mobile-subscribe-button"
                className="mt-2 rounded-full bg-stylist-ink text-white text-sm px-5 py-3"
              >
                Subscribe today
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
